import * as React from 'react'

import { Box, Button, SelectOption, Text } from '@factorialco/deprecated-design-system'
import {
  CreateAssignmentDocument,
  UpdateAssignmentDocument,
  UpdateEmployeePolicyIdDocument,
} from 'generated/resources/graphql'
import capitalize from 'lodash/capitalize'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import Select from 'modules/core/components/Select'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import policies from 'modules/timeoff/store/collections/timeoffPolicies/policies'
import policyTimelines from 'modules/timeoff/store/collections/timeoffPolicies/policyTimelines'
import type Policy from 'modules/timeoff/store/models/TimeoffPolicy/Policy'
import { PolicyTimelineItem } from 'modules/timeoff/store/models/TimeoffPolicy/PolicyTimeline'

interface Props {
  employeeId: string
  timelineItem?: PolicyTimelineItem
  toggleUpsertForm: (value: boolean) => () => void
  fetchTimeline: () => Promise<void>
  isDefaultAssignment: boolean
}

const isCreate = (item: PolicyTimelineItem | undefined, assignment: boolean): item is undefined =>
  !item && !assignment

const UpsertAssignment = ({
  employeeId,
  timelineItem,
  toggleUpsertForm,
  fetchTimeline,
  isDefaultAssignment,
}: Props) => {
  const policyOptions: SelectOption[] = policies.models.map((policy: Policy) => {
    return {
      label: policy.attributes.name,
      value: policy.attributes.id,
    }
  })
  const defaultPolicyOption = policyOptions[0]

  const [timeoffPolicyId, setTimeoffPolicyId] = React.useState<string>(
    timelineItem?.timeoff_policy_id ?? (defaultPolicyOption ? defaultPolicyOption.value : 0)
  )
  const [effectiveAt, setEffectiveAt] = React.useState<string>(
    timelineItem?.start_at || dateToString(moment(), 'backend')
  )

  const trackCreateSubmitEvent = () =>
    track(trackingEvents.timeoff.policyAssignments.createPolicyAssignmentSubmit)

  const trackUpdateSubmitEvent = () =>
    track(trackingEvents.timeoff.policyAssignments.updatePolicyAssignmentSubmit)

  const [createAssignmentMutation] = useMutation(CreateAssignmentDocument, {
    extractorFn: (mutationData) => mutationData.timeoffMutations.createTimeoffPolicyAssignment,
  })

  const [updateAssignmentMutation] = useMutation(UpdateAssignmentDocument, {
    extractorFn: (mutationData) => mutationData.timeoffMutations.updateTimeoffPolicyAssignment,
  })

  const [updateEmployeePolicyIdMutation] = useMutation(UpdateEmployeePolicyIdDocument, {
    extractorFn: (mutationData) => mutationData.employeesMutations.updateEmployeesEmployee,
  })

  const showStructuredErrors = (errors: Record<string, string>) => {
    const message = 'messages' in errors ? errors.messages : Object.values(errors).join(', ')

    if (message) {
      app.setFlash({ type: 'wrong', message, time: 3000 })
    }
  }

  const onSubmit = async () => {
    let success: boolean

    if (isCreate(timelineItem, isDefaultAssignment)) {
      trackCreateSubmitEvent()
      const response = await createAssignmentMutation({
        variables: {
          effectiveAt,
          timeoffPolicyId,
          employeeId,
        },
      })
      success = handleResult(response, showStructuredErrors).success
    } else {
      trackUpdateSubmitEvent()
      if (isDefaultAssignment) {
        const response = await updateEmployeePolicyIdMutation({
          variables: {
            employeeId,
            timeoffPolicyId,
          },
        })
        success = handleResult(response, showStructuredErrors).success
      } else {
        const response = await updateAssignmentMutation({
          variables: {
            id: timelineItem.policy_assignment_id,
            effectiveAt,
            timeoffPolicyId,
          },
        })
        success = handleResult(response, showStructuredErrors).success
      }
    }

    if (success) {
      app.setFlash({ type: 'correct', message: i18n.t('common.saved_successfully'), time: 3000 })
      toggleUpsertForm(false)()
      await fetchTimeline()
    }
  }

  return (
    <Box paddingX="s32" paddingY="s24" gap="s16" background="grey100">
      <Box paddingTop="s8">
        <Text size="400" color="grey1000" weight="medium">
          {isCreate(timelineItem, isDefaultAssignment)
            ? i18n.t('time_off.policy_timeline.create_assignment.title')
            : i18n.t('time_off.policy_timeline.edit_assignment.title')}
        </Text>
      </Box>

      <Text size="300">
        {isCreate(timelineItem, isDefaultAssignment)
          ? i18n.t('time_off.policy_timeline.create_assignment.description')
          : i18n.t('time_off.policy_timeline.edit_assignment.description')}
      </Text>
      <Box paddingTop="s16" maxWidth="s400">
        <Select
          label={i18n.t('common.policy')}
          value={timeoffPolicyId?.toString()}
          onChange={(value) => setTimeoffPolicyId(String(value))}
          options={policyOptions}
        />
      </Box>

      {!isDefaultAssignment && (
        <Box paddingTop="s16">
          <DayPicker
            label={capitalize(i18n.t('common.starting_from'))}
            value={effectiveAt}
            valueFormat="backend"
            onChange={(value) => setEffectiveAt(value)}
            minDate={moment(policyTimelines.timeline?.attributes.start_limit_date)}
          />
        </Box>
      )}

      <Box alignItems="flexEnd">
        <Box flexDirection="row" gap="s16">
          <Button onClick={toggleUpsertForm(false)} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
          <Button onClick={onSubmit}>
            {isCreate(timelineItem, isDefaultAssignment)
              ? i18n.t('time_off.policy_timeline.create_assignment.submit')
              : i18n.t('time_off.policy_timeline.edit_assignment.submit')}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default UpsertAssignment
